import React, {
  createContext,
  useMemo,
  useContext,
} from "react";
import { usePostGetUsers } from "@/api/pizza-checker/users/users";
import { usePermissions } from "@/hooks/use-permissions";
import { isAllBranches } from "@/users/user-utils";

export interface User {
  _id: string;
  login: string;
  first_name?: string;
  last_name?: string;
  role_id: string;
  /** null = access to all branches */
  branch_id: string | null;
  enabled: boolean;
  updated_at?: string;
  created_at?: string;
}

export interface UserPagination {
  has_next: boolean;
  has_prev: boolean;
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

type ApiUser = Omit<User, "branch_id" | "enabled"> & {
  branch_id?: string | null;
  enabled?: boolean;
};

export interface UsersData {
  users: ApiUser[];
  pagination: UserPagination;
}

interface UserContextType {
  users: User[] | undefined;
  usersLoading: boolean;
  refetchUsers: () => void;
}

interface UserProviderProps {
  children: React.ReactNode;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

// Fetch a large page to get all users; filtering happens in the table
const ALL_USERS_REQUEST = { page: 1, page_size: 1000 };

export const UserProvider = ({ children }: UserProviderProps) => {
  // "users" or "users.without_dev" — the same permissions the API checks
  const { isAdmin } = usePermissions();

  const { data, isLoading, refetch } = usePostGetUsers<UsersData>(
    ALL_USERS_REQUEST,
    {
      query: {
        enabled: isAdmin,
      },
    }
  );

  const users = useMemo(
    () =>
      data?.users.map(
        (user): User => ({
          ...user,
          branch_id: isAllBranches(user.branch_id)
            ? null
            : (user.branch_id as string),
          enabled: user.enabled !== false,
        }),
      ),
    [data],
  );

  return (
    <UserContext.Provider
      value={useMemo(
        () => ({
          users,
          usersLoading: isLoading,
          refetchUsers: refetch,
        }),
        [users, isLoading, refetch]
      )}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserProvider");
  }
  return context;
};
